/**
 * product-question.js
 *
 * Powers the "Ask about this product" block. Two jobs:
 *
 *   1. Cycle example questions through the input's placeholder so shoppers
 *      can see what the box is for without reading a label.
 *   2. Take what they type, open the Klink chat widget, write the message
 *      into the widget's own input, and press send.
 *
 * Klink renders as a React app inside a closed-ish shadow root, and exposes no
 * "prefill" API. So step 2 waits for the widget's input to mount, writes to it
 * through React's native value setter (a plain `.value =` assignment gets
 * discarded on the next React render), and then clicks its send button.
 * If send never lands, the text is left sitting in the widget's input, which
 * is a usable fallback: the shopper just presses send themselves.
 */

const KLINK_HOST = '#klink-chat-widget-container';
const KLINK_INPUT = 'textarea, [contenteditable="true"]';
const KLINK_SEND = [
  'button[class*="hover:bg-chat-secondary"]',
  'button[type="submit"]',
  'button[aria-label*="send" i]',
].join(', ');

const MOUNT_TIMEOUT = 12000;
const SEND_ATTEMPTS = 3;

function klinkShadow() {
  const host = document.querySelector(KLINK_HOST);
  return host && host.shadowRoot ? host.shadowRoot : null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Poll `fn` until it returns something truthy, or give up. */
function waitFor(fn, timeout = MOUNT_TIMEOUT, interval = 120) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      let result = null;
      try {
        result = fn();
      } catch (e) {
        /* widget not ready */
      }
      if (result) return resolve(result);
      if (Date.now() - start >= timeout) return resolve(null);
      setTimeout(tick, interval);
    };
    tick();
  });
}

/** Full pointer sequence, so the widget's React handlers actually fire. */
function realClick(el) {
  const opts = { bubbles: true, cancelable: true, composed: true, view: window };
  ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
    const Ctor = type.startsWith('pointer') ? PointerEvent : MouseEvent;
    try {
      el.dispatchEvent(new Ctor(type, opts));
    } catch (e) {
      /* older browser, skip this event type */
    }
  });
}

/** Write a value React will keep. */
function setReactValue(el, value) {
  if (el.isContentEditable) {
    el.focus();
    el.textContent = value;
    el.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        composed: true,
        data: value,
        inputType: 'insertText',
      })
    );
    return;
  }

  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

  el.focus();
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  el.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
}

function pressEnter(el) {
  ['keydown', 'keypress', 'keyup'].forEach((type) => {
    el.dispatchEvent(
      new KeyboardEvent(type, {
        key: 'Enter',
        code: 'Enter',
        keyCode: 13,
        which: 13,
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  });
}

function readValue(el) {
  return (el.isContentEditable ? el.textContent : el.value) || '';
}

/**
 * Get the widget loaded and its panel open.
 *
 * All the open logic lives in klink.liquid so there is exactly one thing on the
 * page deciding when to open. If we also called openWidget() here we would race
 * its polling loop and toggle the panel back shut. We just ask it to open and
 * then wait for the widget's input to mount, which is our signal it worked.
 */
async function openKlink() {
  if (typeof window.__openKlinkChat === 'function') {
    window.__openKlinkChat();
    return true;
  }
  if (typeof window.startKlinkChat === 'function') {
    window.startKlinkChat();
    return true;
  }
  return false;
}

/**
 * Open Klink and send `message`.
 * Resolves 'sent', 'typed' (in the box but not sent), or 'unavailable'.
 */
async function sendToKlink(message) {
  const opened = await openKlink();
  if (!opened) return 'unavailable';

  const field = await waitFor(() => {
    const shadow = klinkShadow();
    return shadow ? shadow.querySelector(KLINK_INPUT) : null;
  });

  if (!field) return 'unavailable';

  for (let attempt = 0; attempt < SEND_ATTEMPTS; attempt++) {
    setReactValue(field, message);
    // Let React commit the state update before we hunt for an enabled button.
    await sleep(150);

    const shadow = klinkShadow();
    const send = shadow ? shadow.querySelector(KLINK_SEND) : null;

    if (send && !send.disabled) {
      realClick(send);
    } else {
      pressEnter(field);
    }

    await sleep(400);
    // A cleared input means the widget accepted the message.
    if (!readValue(field).trim()) return 'sent';
  }

  return 'typed';
}

class ProductQuestion extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-pq-input]');
    this.button = this.querySelector('[data-pq-submit]');
    this.status = this.querySelector('[data-pq-status]');

    if (!this.input || !this.button) return;

    // Not `this.prefix`: Element.prefix is a read-only getter and assigning throws.
    this.contextPrefix = this.dataset.contextPrefix || '';
    this.busy = false;
    this.paused = false;
    this.visible = true;
    this.timer = null;

    this.button.addEventListener('click', () => this.submit());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submit();
      }
    });

    this.startPlaceholders();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    if (this.observer) this.observer.disconnect();
  }

  async submit() {
    const question = this.input.value.trim();

    if (!question) {
      this.input.focus();
      return;
    }
    if (this.busy) return;

    this.busy = true;
    this.paused = true;
    this.button.disabled = true;
    this.setStatus('Opening chat');

    const result = await sendToKlink(this.contextPrefix + question);

    if (result === 'sent') {
      this.input.value = '';
      this.setStatus('Sent to chat');
      this.paused = false;
    } else if (result === 'typed') {
      this.input.value = '';
      this.setStatus('Your question is in the chat window. Press send to ask it.');
      this.paused = false;
    } else {
      this.setStatus('Chat is not loading right now. Try the chat button in the corner.');
    }

    this.busy = false;
    this.button.disabled = false;
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  startPlaceholders() {
    const raw = this.dataset.examples || '';
    this.examples = raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (!this.examples.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || this.dataset.animate === 'false') {
      this.input.placeholder = this.examples[0];
      return;
    }

    this.input.addEventListener('focus', () => {
      this.paused = true;
      this.input.placeholder = '';
    });
    this.input.addEventListener('blur', () => {
      if (!this.input.value && !this.busy) this.paused = false;
    });

    if ('IntersectionObserver' in window) {
      this.visible = false;
      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      this.observer.observe(this);
    }

    this.typeLoop();
  }

  wait(ms) {
    return new Promise((resolve) => {
      this.timer = setTimeout(resolve, ms);
    });
  }

  /** Type each example out, hold it, delete it, move to the next. */
  async typeLoop() {
    let index = 0;

    while (this.isConnected) {
      while ((this.paused || !this.visible) && this.isConnected) {
        await this.wait(400);
      }
      if (!this.isConnected) return;

      const text = this.examples[index % this.examples.length];
      const interrupted = () => this.paused || !this.visible || !this.isConnected;

      for (let i = 1; i <= text.length; i++) {
        if (interrupted()) break;
        this.input.placeholder = text.slice(0, i);
        await this.wait(45);
      }

      if (!interrupted()) await this.wait(1900);

      for (let i = text.length; i >= 0; i--) {
        if (interrupted()) break;
        this.input.placeholder = text.slice(0, i);
        await this.wait(20);
      }

      if (!interrupted()) await this.wait(260);
      index++;
    }
  }
}

if (!customElements.get('product-question')) {
  customElements.define('product-question', ProductQuestion);
}